import * as React from 'react';
import { IMask } from 'react-imask';

import { cn } from '@/libs';
import { useNativeInput } from '@/forms/hooks';

import { useClickAway } from '../hooks.tsx';
import { Icon } from '../icon/icon';
import { Input, MaskedInputProps } from '../input/input';

import './time-selector.css';

type TimeSelectorProps = Omit<
  MaskedInputProps,
  'placeholder' | 'onComplete' | 'onAccept' | 'mask' | 'blocks' | 'trailingIcon'
>;

type Period = 'AM' | 'PM';

type TimeParts12 = {
  hour: number; // 1-12
  minute: number; // 0-59
  period: Period;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

const parseTime12 = (value?: string): TimeParts12 | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const match = /^(\d{1,2}):(\d{2})\s*([AP]M)$/i.exec(trimmed);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3].toUpperCase() as Period;

  if (!Number.isFinite(hour) || hour < 1 || hour > 12) return null;
  if (!Number.isFinite(minute) || minute < 0 || minute > 59) return null;
  if (period !== 'AM' && period !== 'PM') return null;

  return { hour, minute, period };
};

const formatTime12 = ({ hour, minute, period }: TimeParts12) =>
  `${pad2(hour)}:${pad2(minute)} ${period}`;

const TimeSelector = React.forwardRef<HTMLInputElement, TimeSelectorProps>(
  (
    {
      id,
      label,
      onChange,
      onBlur,
      name,
      value,
      state,
      stateText,
      stateDefaultIcon,
      className,
      disabled,
    },
    forwardedRef,
  ) => {
    const clearRef = React.useRef<boolean>(false);
    const ref = React.useRef<HTMLInputElement>(null);
    const hiddenInputRef = React.useRef<HTMLInputElement>(null);

    const containerClasses = cn('ts-wrapper', className);

    const [time, setTime] = React.useState<string | undefined>(value);
    const [isOpen, setIsOpen] = React.useState(false);
    const [parts, setParts] = React.useState<TimeParts12>({
      hour: 12,
      minute: 0,
      period: 'AM',
    });

    const { setNativeValue } = useNativeInput(name ?? '', 'text');

    const wrapperRef = useClickAway(() => {
      setIsOpen(false);
    });

    const panelRef = React.useRef<HTMLDivElement | null>(null);
    const [panelPlacement, setPanelPlacement] = React.useState<
      'bottom' | 'top'
    >('bottom');

    const hoursColRef = React.useRef<HTMLDivElement | null>(null);
    const minutesColRef = React.useRef<HTMLDivElement | null>(null);

    React.useImperativeHandle(
      forwardedRef,
      () => ref.current as HTMLInputElement,
    );

    // A cleared value must empty the box too, and '' rather than undefined, which react-imask
    // ignores. The hidden input is reset as well, or React ignores re-entering the time it last
    // held. Re-running on `disabled` drops a partly typed time the form value never held.
    React.useEffect(() => {
      const next = value ?? '';
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = next;
      }
      setTime(next);
    }, [value, disabled]);

    React.useEffect(() => {
      const parsed = parseTime12(time);
      if (parsed) {
        setParts(parsed);
      }
    }, [time]);

    React.useEffect(() => {
      if (!isOpen) return;

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
        }
      };

      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen]);

    React.useEffect(() => {
      if (!isOpen) return;

      const computePlacement = () => {
        const anchor = wrapperRef.current;
        const panel = panelRef.current;
        if (!anchor || !panel) return;

        const rect = anchor.getBoundingClientRect();
        const viewportHeight =
          window.visualViewport?.height ?? window.innerHeight ?? rect.bottom;
        const panelHeight = panel.getBoundingClientRect().height;
        const gap = 12;

        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        const shouldFlip =
          spaceBelow < panelHeight + gap && spaceAbove > spaceBelow;

        setPanelPlacement(shouldFlip ? 'top' : 'bottom');
      };

      const raf = window.requestAnimationFrame(computePlacement);

      window.addEventListener('resize', computePlacement);
      window.addEventListener('scroll', computePlacement, true);

      return () => {
        window.cancelAnimationFrame(raf);
        window.removeEventListener('resize', computePlacement);
        window.removeEventListener('scroll', computePlacement, true);
      };
    }, [isOpen, wrapperRef]);

    React.useEffect(() => {
      if (!isOpen) return;

      // Avoid `element.scrollIntoView()` here: it can scroll the window.
      // We only want to scroll inside the hours/minutes columns.
      const scrollActiveIntoView = (col: HTMLDivElement | null) => {
        const active = col?.querySelector(
          '.ts-option--active',
        ) as HTMLElement | null;
        if (!col || !active) return;

        const activeTop = active.offsetTop;
        const activeBottom = activeTop + active.offsetHeight;
        const visibleTop = col.scrollTop;
        const visibleBottom = visibleTop + col.clientHeight;

        let nextScrollTop = visibleTop;
        if (activeTop < visibleTop) {
          nextScrollTop = activeTop;
        } else if (activeBottom > visibleBottom) {
          nextScrollTop = activeBottom - col.clientHeight;
        } else {
          return;
        }

        const maxScrollTop = Math.max(0, col.scrollHeight - col.clientHeight);
        col.scrollTop = Math.min(maxScrollTop, Math.max(0, nextScrollTop));
      };

      const raf = window.requestAnimationFrame(() => {
        scrollActiveIntoView(hoursColRef.current);
        scrollActiveIntoView(minutesColRef.current);
      });

      return () => window.cancelAnimationFrame(raf);
    }, [isOpen, parts.hour, parts.minute]);

    const commitParts = (next: TimeParts12) => {
      const nextValue = formatTime12(next);
      clearRef.current = true;
      setParts(next);
      setTime(nextValue);
      setNativeValue(nextValue);
    };

    return (
      <div className={containerClasses} ref={wrapperRef}>
        <input
          type="text"
          className="ts-hidden-input"
          aria-hidden="true"
          aria-label="Time value"
          name={name}
          tabIndex={-1}
          onChange={onChange}
          ref={hiddenInputRef}
          onBlur={onBlur}
        />
        <Input
          ref={ref}
          id={id}
          label={label}
          mask="hh:mm AA"
          placeholder="hh:mm AM"
          value={time}
          disabled={disabled}
          blocks={{
            hh: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 12,
            },
            mm: {
              mask: IMask.MaskedRange,
              from: 0,
              to: 59,
            },
            AA: {
              mask: IMask.MaskedEnum,
              enum: ['AM', 'PM', 'am', 'pm'],
              prepareChar: (str: string) => str.toUpperCase(),
            },
          }}
          trailingIcon={
            <span aria-hidden="true">
              <Icon
                type="timer"
                size="large"
                color="var(--color_neutral-light)"
              />
            </span>
          }
          onComplete={newValue => {
            clearRef.current = true;
            setNativeValue(newValue);
          }}
          onAccept={newValue => {
            if (newValue === '' && clearRef.current) {
              clearRef.current = false;
              setTime(undefined);
              setNativeValue('');
            } else {
              setTime(newValue);
            }
          }}
          onFocus={() => {
            if (disabled) return;
            setIsOpen(true);
          }}
          onClick={() => {
            if (disabled) return;
            setIsOpen(true);
          }}
          onBlur={() => {
            if (hiddenInputRef.current) {
              hiddenInputRef.current.focus();
              hiddenInputRef.current.blur();
            }
          }}
          state={state}
          stateText={stateText}
          stateDefaultIcon={stateDefaultIcon}
        />
        {isOpen && !disabled && (
          <div
            className={cn('ts-panel', {
              'ts-panel--top': panelPlacement === 'top',
            })}
            role="dialog"
            aria-label="Time picker"
            ref={panelRef}
          >
            <div className="ts-period-row" role="group" aria-label="AM PM">
              <div className="ts-period-row__inner">
                {(['AM', 'PM'] as const).map(p => (
                  <button
                    key={`p_${p}`}
                    type="button"
                    className={cn('ts-option', 'ts-option--period', {
                      'ts-option--active': parts.period === p,
                    })}
                    data-selected={parts.period === p ? 'true' : 'false'}
                    onClick={() => commitParts({ ...parts, period: p })}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="ts-header-row" aria-hidden="true">
              <div className="ts-header-cell">Hours</div>
              <div className="ts-header-cell">Minutes</div>
            </div>
            <div className="ts-columns" role="group" aria-label="Time options">
              <div
                className="ts-col"
                role="listbox"
                aria-label="Hours"
                ref={hoursColRef}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                  <button
                    key={`h_${h}`}
                    type="button"
                    className={cn('ts-option', {
                      'ts-option--active': parts.hour === h,
                    })}
                    role="option"
                    data-selected={parts.hour === h ? 'true' : 'false'}
                    onClick={() => commitParts({ ...parts, hour: h })}
                  >
                    {pad2(h)}
                  </button>
                ))}
              </div>
              <div
                className="ts-col"
                role="listbox"
                aria-label="Minutes"
                ref={minutesColRef}
              >
                {Array.from({ length: 60 }, (_, i) => i).map(m => (
                  <button
                    key={`m_${m}`}
                    type="button"
                    className={cn('ts-option', {
                      'ts-option--active': parts.minute === m,
                    })}
                    role="option"
                    data-selected={parts.minute === m ? 'true' : 'false'}
                    onClick={() => commitParts({ ...parts, minute: m })}
                  >
                    {pad2(m)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export { TimeSelector };
